import { useRef } from 'react'
import { useVoiceStore } from '../../stores/voiceStore'
import VoiceChannelSidePanel from './VoiceChannelSidePanel'

// Tab-bar-only height when collapsed — matches the old `h-9` (36px) so
// the collapsed strip looks identical to before OSPROD-115.
const COLLAPSED_HEIGHT = 36
const MIN_HEIGHT = 120
const HANDLE_HEIGHT = 8
// Leave room for the voice grid + control bar above so the panel can't
// be dragged to swallow the whole call surface.
const BOTTOM_RESERVE = 220

/**
 * OSPROD-115: wraps the voice-channel chat/transcript side panel with a
 * draggable top edge so users can enlarge it during a call to read GIFs,
 * images, video previews and long messages without constant scrolling.
 *
 * The panel sits at the bottom of the call column, so dragging the handle
 * UP grows it. Height is persisted in voiceStore (localStorage) and shared
 * across the grid / speaker / focused layouts, so switching modes or
 * reloading keeps the user's chosen size. The collapsed state still wins:
 * when collapsed only the tab bar shows and the handle is hidden.
 */
export default function ResizableSidePanel({ channelId }: { channelId: string }) {
  const isCollapsed = useVoiceStore((s) => s.isSidePanelCollapsed)
  const height = useVoiceStore((s) => s.sidePanelHeight)
  const setHeight = useVoiceStore((s) => s.setSidePanelHeight)
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null)

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    dragRef.current = { startY: e.clientY, startHeight: height }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return
    const delta = drag.startY - e.clientY // up = grow
    const max = Math.max(MIN_HEIGHT, window.innerHeight - BOTTOM_RESERVE)
    const next = Math.min(max, Math.max(MIN_HEIGHT, drag.startHeight + delta))
    setHeight(next)
  }

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return
    e.currentTarget.releasePointerCapture(e.pointerId)
    dragRef.current = null
  }

  if (isCollapsed) {
    return (
      <div className="shrink-0" style={{ height: COLLAPSED_HEIGHT }}>
        <VoiceChannelSidePanel channelId={channelId} />
      </div>
    )
  }

  return (
    <div className="shrink-0 flex flex-col" style={{ height }}>
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize chat panel"
        title="Drag to resize"
        className="group flex shrink-0 cursor-row-resize items-center justify-center"
        style={{ height: HANDLE_HEIGHT, touchAction: 'none' }}
      >
        <div className="h-1 w-10 rounded-full bg-border transition group-hover:bg-muted-foreground/60" />
      </div>
      <div className="min-h-0 flex-1">
        <VoiceChannelSidePanel channelId={channelId} />
      </div>
    </div>
  )
}
